import { useState, useCallback, useEffect } from 'react';
import { BrowserProvider } from 'ethers';
import { BASE_CHAIN_ID, BASE_CHAIN_ID_HEX, BASE_NETWORK_CONFIG } from '../constants';

export const useWallet = () => {
  const [account, setAccount] = useState('');
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [provider, setProvider] = useState(null);
  const [error, setError] = useState(null);
  const [chainId, setChainId] = useState(null);

  // Check if already connected on mount
  useEffect(() => {
    const checkConnection = async () => {
      if (typeof window.ethereum !== 'undefined') {
        try {
          const accounts = await window.ethereum.request({ 
            method: 'eth_accounts' 
          });
          if (accounts.length > 0) {
            const browserProvider = new BrowserProvider(window.ethereum);
            const network = await browserProvider.getNetwork();
            
            setAccount(accounts[0]);
            setConnected(true);
            setProvider(browserProvider);
            setChainId(network.chainId);
          }
        } catch (err) {
          console.error('Error checking connection:', err);
        }
      }
    };
    checkConnection();
  }, []);

  // Listen for account and chain changes
  useEffect(() => {
    if (typeof window.ethereum === 'undefined') return;

    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        setAccount('');
        setConnected(false);
        setProvider(null);
      } else {
        setAccount(accounts[0]);
        setConnected(true);
      }
    };

    const handleChainChanged = (chainIdHex) => {
      setChainId(BigInt(chainIdHex));
      // Reload provider
      const browserProvider = new BrowserProvider(window.ethereum);
      setProvider(browserProvider);
    };

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);

    return () => {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
    };
  }, []);

  const connect = useCallback(async () => {
    if (typeof window.ethereum === 'undefined') {
      setError('Please install MetaMask to use this app');
      return false;
    }

    setConnecting(true);
    setError(null);

    try {
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });

      const browserProvider = new BrowserProvider(window.ethereum);
      const network = await browserProvider.getNetwork();

      setAccount(accounts[0]);
      setConnected(true);
      setProvider(browserProvider);
      setChainId(network.chainId);
      
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setConnecting(false);
    }
  }, []);

  const switchToBase = useCallback(async () => {
    if (!window.ethereum) return false;

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: BASE_CHAIN_ID_HEX }],
      });
      return true;
    } catch (switchError) {
      // Chain not added to MetaMask
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [BASE_NETWORK_CONFIG],
          });
          return true;
        } catch (addError) {
          setError('Failed to add Base network');
          return false;
        }
      }
      setError('Failed to switch to Base network');
      return false;
    }
  }, []);

  const isOnBase = chainId === BASE_CHAIN_ID;

  const disconnect = useCallback(() => {
    setAccount('');
    setConnected(false);
    setProvider(null);
    setChainId(null);
  }, []);

  return {
    account,
    connected,
    connecting,
    provider,
    error,
    chainId,
    isOnBase,
    connect,
    switchToBase,
    disconnect,
  };
};
